import React, { useEffect } from 'react'
import { useOrganizationFromDomain } from '../../hooks/useOrganizationFromDomain'

interface CustomerThemeWrapperProps {
  children: React.ReactNode
}

export function CustomerThemeWrapper({ children }: CustomerThemeWrapperProps) {
  const { organization: domainOrg, loading: orgLoading } = useOrganizationFromDomain()
  
  // Apply organization theme to CSS variables
  useEffect(() => {
    if (domainOrg?.settings?.branding) {
      const branding = domainOrg.settings.branding
      const root = document.documentElement
      
      console.log('🎨 Applying organization theme:', {
        organizationId: domainOrg.id,
        organizationName: domainOrg.name,
        primaryColor: branding.primaryColor
      })
      
      // Primary colors
      root.style.setProperty('--primary-color', branding.primaryColor || '#9CA3AF')
      root.style.setProperty('--background-color', branding.backgroundColor || '#0F0F0F')
      root.style.setProperty('--accent-color', branding.accentColor || '#9CA3AF')
      root.style.setProperty('--text-color', branding.textColor || '#FFFFFF')
      
      // Also set the new naming convention for compatibility
      root.style.setProperty('--color-primary', branding.primaryColor || '#9CA3AF')
      root.style.setProperty('--color-background', branding.backgroundColor || '#0F0F0F')
      root.style.setProperty('--color-accent', branding.accentColor || '#9CA3AF')
      root.style.setProperty('--color-text', branding.textColor || '#FFFFFF')
      
      // RGB version for opacity
      const hexToRgb = (hex: string): string => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
        return result 
          ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
          : '0, 0, 0'
      }
      
      root.style.setProperty('--primary-rgb', hexToRgb(branding.primaryColor || '#9CA3AF'))
      root.style.setProperty('--color-primary-rgb', hexToRgb(branding.primaryColor || '#9CA3AF'))
      
      // Derived colors
      const darkenColor = (hex: string, percent: number): string => {
        const num = parseInt(hex.replace('#', ''), 16)
        const amt = Math.round(2.55 * percent * 100)
        const R = (num >> 16) - amt
        const G = (num >> 8 & 0x00FF) - amt
        const B = (num & 0x0000FF) - amt
        return '#' + (0x1000000 + (R > 255 ? 255 : R < 0 ? 0 : R) * 0x10000 +
          (G > 255 ? 255 : G < 0 ? 0 : G) * 0x100 +
          (B > 255 ? 255 : B < 0 ? 0 : B)).toString(16).slice(1)
      }
      
      const lightenColor = (hex: string, percent: number): string => {
        const num = parseInt(hex.replace('#', ''), 16)
        const amt = Math.round(2.55 * percent * 100)
        const R = (num >> 16) + amt
        const G = (num >> 8 & 0x00FF) + amt  
        const B = (num & 0x0000FF) + amt
        return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
          (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
          (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1)
      }
      
      root.style.setProperty('--primary-hover', darkenColor(branding.primaryColor || '#9CA3AF', 0.1))
      root.style.setProperty('--primary-dark', darkenColor(branding.primaryColor || '#9CA3AF', 0.2))
      root.style.setProperty('--primary-light', lightenColor(branding.primaryColor || '#9CA3AF', 0.2))
      
      // Background variants
      root.style.setProperty('--background-light', lightenColor(branding.backgroundColor || '#0F0F0F', 0.1))
      root.style.setProperty('--background-dark', darkenColor(branding.backgroundColor || '#0F0F0F', 0.1))
      
      // Border colors
      root.style.setProperty('--border-color', `rgba(${hexToRgb(branding.primaryColor || '#9CA3AF')}, 0.2)`)
      root.style.setProperty('--border-color-light', `rgba(${hexToRgb(branding.primaryColor || '#9CA3AF')}, 0.3)`)
      
      // Card background
      root.style.setProperty('--card-background', 'rgba(255, 255, 255, 0.05)')
      
      // Update page title
      document.title = branding.brandName || domainOrg.name || 'Customer Portal'
      
      // Apply to body
      document.body.style.backgroundColor = branding.backgroundColor || '#0F0F0F'
      document.body.style.color = branding.textColor || '#FFFFFF'
    }
  }, [domainOrg])
  
  // Show loading state while detecting organization
  if (orgLoading) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400 mx-auto mb-4"></div>
            <p className="text-gray-400">Loading...</p>
          </div>
        </div>
      </div>
    )
  }
  
  return <>{children}</>
}